import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { EscrowInvitationForm, type IEscrowInvitationForm } from '@/components/Escrow/Invitation';
import { type IEscrowInvitation } from 'src/graphql/schema';
import { EscrowInvitationCreateDocument } from 'src/graphql/actions/escrow/escrowInvitationCreate.mutation';
import { getApiError, ClientError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';
import { addEscrowInvitationCache } from 'src/graphql/client/cache';

import { useEscrowInvitationContext } from '../model';

const CreateInvitationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [createEscrowInvitation] = useMutation(EscrowInvitationCreateDocument);
  const { escrowId, invitePartyType } = useEscrowInvitationContext();

  const onSubmit = async ({ userEmail, userName, companyName }: IEscrowInvitationForm) => {
    try {
      const { data } = await createEscrowInvitation({
        variables: {
          escrowId,
          escrowInvitationParams: {
            partyType: invitePartyType,
            companyName,
            userEmail,
            userName,
          },
        },
      });

      if (data?.escrowInvitationCreate?.success) {
        toast.success('Saved!');
        setIsOpen(false);
        addEscrowInvitationCache(data.escrowInvitationCreate.escrowInvitation as IEscrowInvitation);
      } else {
        throw new ClientError(data?.escrowInvitationCreate?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">
          <Plus />
          Add info
        </Button>
      </DialogTrigger>
      <DialogContent className="md:max-w-[374px] sm:max-w-[374px]">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-base font-bold">
            Add the {invitePartyType} organization & their primary contact
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6A8493]">
            Add the organization’s details and the primary contact that will be invited to the Codekeeper platform to
            manage this escrow.
          </DialogDescription>
        </DialogHeader>
        <EscrowInvitationForm
          onSubmit={onSubmit}
          onCancel={onCancel}
          defaultValues={{
            userName: '',
            userEmail: '',
            companyName: '',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvitationDialog;
